import { useCallback, useEffect, useState } from "react";
import { accountService, homeService } from "@miplatform/api";
import { AutocompleteOption } from "@miplatform/components";
import {
  ACCOUNT_TYPE,
  ATTEMPT_STATUSES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IAccountResponse,
  ICommunityListItemResponse,
} from "@miplatform/types";

const STATUS_OPTIONS: AutocompleteOption[] = [
  { label: "New", value: ATTEMPT_STATUSES.NEW },
  // TODO: AB: Unable to Auto-Assign is not a valid status, we need to add it to the API
  { label: "Unable to Auto-Assign", value: ATTEMPT_STATUSES.PENDING },
  { label: "Complete", value: ATTEMPT_STATUSES.SUBMITTED },
  { label: "On Hold", value: ATTEMPT_STATUSES.HOLD },
  { label: "Late", value: ATTEMPT_STATUSES.PLANNED },
];

export const useCalendarFilterGroup = () => {
  const stateManager = useComponentState();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<CalendarFilters>({
    status: null,
    builder: null,
    community: null,
    dueDate: null,
  });
  const [appliedFilters, setAppliedFilters] = useState<CalendarFilters>({
    status: null,
    builder: null,
    community: null,
    dueDate: null,
  });
  const [builderOptions, setBuilderOptions] = useState<AutocompleteOption[]>(
    []
  );
  const [communityOptions, setCommunityOptions] = useState<
    AutocompleteOption[]
  >([]);

  const loadOptions = () => {
    stateManager.run(async () => {
      const [buildersResponse, communitiesResponse] = await Promise.all([
        accountService.accounts.getAll({
          sort: "name",
          filters: {
            accountType: {
              in: [ACCOUNT_TYPE.BUILDER],
            },
          },
        }),
        homeService.communities.getAll({
          sort: "name",
        }),
      ]);

      if (buildersResponse.status === HTTP_STATUSES.OK) {
        setBuilderOptions(
          (buildersResponse.data as IAccountResponse[]).map((customer) => ({
            label: customer.name,
            value: customer.id,
          }))
        );
      }

      if (communitiesResponse.status === HTTP_STATUSES.OK) {
        setCommunityOptions(
          (communitiesResponse.data as ICommunityListItemResponse[]).map(
            (community) => ({
              label: community.name,
              value: community.id,
            })
          )
        );
      }
    });
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const handleFilterChange = useCallback(
    <K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [setFilters]
  );

  const appliedFilterLabels = [
    appliedFilters.status?.label,
    appliedFilters.builder?.label,
    appliedFilters.community?.label,
  ].filter(Boolean) as string[];

  const hasPendingFilters =
    !!filters.status ||
    !!filters.builder ||
    !!filters.community ||
    !!filters.dueDate ||
    appliedFilterLabels.length > 0;

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    toggleFilters();
  };

  const handleResetFilters = () => {
    const emptyFilters: CalendarFilters = {
      status: null,
      builder: null,
      community: null,
      dueDate: null,
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  return {
    showFilters,
    toggleFilters,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    appliedFilterLabels,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    builderOptions,
    communityOptions,
    statusOptions: STATUS_OPTIONS,
    isLoading: stateManager.isBusy(),
    handleFilterChange,
  };
};

export type CalendarFilterGroupController = ReturnType<
  typeof useCalendarFilterGroup
>;

export type CalendarFilters = {
  status: AutocompleteOption | null;
  builder: AutocompleteOption | null;
  community: AutocompleteOption | null;
  dueDate: moment.Moment | null;
};
